import AsyncValidator from 'async-validator';
import isUndefined from 'lodash/isUndefined.js';
import PendoCollapseTransition from '../../utils/pendo-collapse-transition.vue.js';
import { getPropByPath, getRuleForTrigger, noop } from './utils.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoFormItem',
  components: {
    PendoCollapseTransition
  },
  provide() {
    return {
      $formItem: this
    };
  },
  inject: {
    $form: {
      default: ''
    }
  },
  props: {
    label: {
      type: String,
      default: undefined
    },
    prop: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean,
      default: undefined
    },
    rules: {
      type: [Object, Array],
      default: undefined
    },
    error: {
      type: String,
      default: undefined
    },
    for: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      validateState: '',
      validateMessage: '',
      validateDisabled: false,
      validator: {},
      invalid: false,
      dirty: false
    };
  },
  computed: {
    allRules() {
      let parentFormRules = this.$form.rules;
      const selfRules = this.rules;
      const requiredRule = !isUndefined(this.required) ? {
        required: Boolean(this.required)
      } : [];
      const prop = getPropByPath(parentFormRules, this.prop);
      parentFormRules = parentFormRules ? prop.obj[this.prop || ''] || prop.value : [];
      return [].concat(selfRules || parentFormRules || []).concat(requiredRule);
    },
    shouldValidate() {
      return Boolean(this.allRules.length) || this.isRequired;
    },
    labelFor() {
      return this.for || `form-item--${this._uid}`;
    },
    labelId() {
      return `form-item--${this._uid}-label`;
    },
    errorMessageId() {
      return `form-item--${this._uid}-error`;
    },
    ariaDescribedBy() {
      return this.showErrorMessage ? this.errorMessageId : undefined;
    },
    fieldValue() {
      if (!this.$form.model || !this.prop) {
        return;
      }
      return getPropByPath(this.$form.model, this.prop, true).value;
    },
    isRequired() {
      if (this.required) {
        return true;
      }
      const rules = this.allRules;
      if (rules && rules.length) {
        return rules.some(rule => rule.required);
      }
      return false;
    },
    isValidating() {
      return this.validateState === 'validating';
    },
    isSuccess() {
      return this.validateState === 'success';
    },
    isError() {
      return this.validateState === 'error';
    },
    showErrorMessage() {
      return this.dirty && this.invalid && this.isError;
    }
  },
  watch: {
    error: {
      immediate: true,
      handler(value) {
        this.validateMessage = value;
        this.validateState = value ? 'error' : '';
      }
    }
  },
  mounted() {
    if (this.$form) {
      this.$form.register(this);
    }
    if (this.prop) {
      this.setInitialFieldValue();
    }
  },
  beforeDestroy() {
    if (this.$form) {
      this.$form.unregister(this);
    }
  },
  methods: {
    touch() {
      this.dirty = true;
    },
    setInitialFieldValue() {
      let initialValue = this.fieldValue;
      if (Array.isArray(initialValue)) {
        initialValue = [].concat(initialValue);
      }
      Object.defineProperty(this, 'initialValue', {
        value: initialValue
      });
    },
    validate(trigger) {
      let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
      this.validateDisabled = false;
      const rules = getRuleForTrigger(this.allRules, trigger);
      if (!rules || rules.length === 0) {
        callback();
        return true;
      }
      this.validateState = 'validating';
      rules.forEach(rule => {
        delete rule.trigger;
      });
      const descriptor = {
        [this.prop]: rules
      };
      const validator = new AsyncValidator(descriptor);
      const model = {
        [this.prop]: this.fieldValue
      };
      validator.validate(model, {
        firstFields: true,
        suppressWarning: true
      }, (errors, invalidFields) => {
        this.invalid = Boolean(errors);
        const validateMessage = errors ? errors[0].message : '';
        if (this.dirty) {
          this.validateState = errors ? 'error' : 'success';
          this.validateMessage = validateMessage;
        }
        callback(validateMessage, invalidFields);
        if (this.$form) {
          this.$form.$emit('validate', this.prop, !errors, validateMessage || null);
        }
      });
    },
    resetValidation() {
      this.validateState = '';
      this.validateMessage = '';
      this.validateDisabled = false;
      this.dirty = false;
    },
    reset: _async(function () {
      const _this = this;
      _this.validateState = '';
      _this.validateMessage = '';
      _this.dirty = false;
      const value = _this.fieldValue;
      const prop = getPropByPath(_this.$form.model, _this.prop, true);
      _this.validateDisabled = true;
      if (Array.isArray(value)) {
        prop.obj[prop.key] = [].concat(_this.initialValue);
      } else {
        prop.obj[prop.key] = _this.initialValue;
      }
      return _await(_this.$nextTick(), function () {
        _this.validateDisabled = false;
      });
    }),
    onFieldBlur() {
      if (this.shouldValidate) {
        this.touch();
        this.validate('blur');
      }
    },
    onFieldChange() {
      if (!this.shouldValidate) {
        return;
      }
      if (this.validateDisabled) {
        this.validateDisabled = false;
        return;
      }
      this.touch();
      this.validate('change');
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-form-item.vue2.js.map
